import { createContext, useState, useEffect, useRef, useCallback } from "react";
import api from "../api/axios";

import { signOut, loginById } from "../api/authService";
import { get_profile, get_profile_all_plans } from "../api/userService";
import User from "../models/User";
import { attach_employer } from "../api/employerService";
import { send_message } from "../api/userService";
import { get } from "lodash";
import { toast } from "react-toastify";
import { storage } from "../helpers/utils";
import { Box, CircularProgress } from "@mui/material";
import { getUrl } from "../scenes/pages/employer/employer-flow/employerNavigation";
import { MAX_BENEFIT_AGE_LIST } from "../constants/maxBenefitAges";
export const EMAIL_DOMAIN_BLACKLIST = ["mailinator", "business"];
const ROLE_TYPES = {
  ADMIN: "Admin",
  EMPLOYER: "Employer",
  USER: "User",
};

const determineRoleType = (payload) => {
  if (!payload) return null;
  if (payload.roles?.includes("SYSADMIN")) return ROLE_TYPES.ADMIN;
  if (get(payload, "employerAdminEmployer.id")) return ROLE_TYPES.EMPLOYER;
  return ROLE_TYPES.USER;
};

const ADMIN_SNAPSHOT_KEY = "admin-user";
export const IMPERSONATION_START_IDX_KEY = "impersonation-start-idx";

const clearRoleSession = () => {
  storage.remove("role");
  storage.remove("activeRole");
  storage.remove("userData");
  ["admin", "employer", "user"].forEach((type) => {
    storage.remove(`${type}Token`);
    storage.remove(`${type}UserData`);
  });
};

const persistRoleSession = (payload) => {
  const roleType = determineRoleType(payload);
  if (!roleType) return;

  clearRoleSession();

  const normalizedRole = roleType.toLowerCase();
  const token = payload?.token || storage.read("token");

  storage.write("role", roleType);
  storage.write("activeRole", roleType);
  if (token) {
    storage.write(`${normalizedRole}Token`, token);
    storage.write("token", token);
  }

  try {
    storage.writeJSON("userData", payload);
    storage.writeJSON(`${normalizedRole}UserData`, payload);
  } catch (error) {
    console.error("Failed to store user data in localStorage:", error);
  }
};

export const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [employerPlanId, setEmployerPlanId] = useState(null);
  const [permissibleClassId, setPermissibleClassId] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [stoppedTtcSourceIds, setStoppedTtcSourceIds] = useState([]);
  const stoppedTtcUserIdRef = useRef(user?.id ?? null);

  const markTtcSourceStopped = useCallback((sourceExpenseId) => {
    if (sourceExpenseId == null) return;
    setStoppedTtcSourceIds((current) =>
      current.some((id) => String(id) === String(sourceExpenseId))
        ? current
        : [...current, sourceExpenseId],
    );
  }, []);

  const markTtcSourceActive = useCallback((sourceExpenseId) => {
    if (sourceExpenseId == null) return;
    setStoppedTtcSourceIds((current) =>
      current.filter((id) => String(id) !== String(sourceExpenseId)),
    );
  }, []);

  const isImpersonating = Boolean(storage.readJSON(ADMIN_SNAPSHOT_KEY));

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (stoppedTtcUserIdRef.current !== currentUserId) {
      stoppedTtcUserIdRef.current = currentUserId;
      setStoppedTtcSourceIds([]);
    }
  }, [user?.id]);

  const setUserSafe = useCallback((payload, callback) => {
    if (!payload) {
      setUser(null);
      clearRoleSession();
      callback?.();
      return;
    }
    const instance = payload instanceof User ? payload : User.fromApi(payload);
    setUser(instance);
    persistRoleSession(instance);

    // Use setTimeout to ensure state update has been processed
    if (callback) {
      setTimeout(() => callback(instance), 0);
    }
  }, []);
  const setRefreshUserProfile = () => {
    sessionStorage.setItem("needsUserRefresh", "true");
  };

  const refreshUserProfile = async (extra_data = {}) => {
    return new Promise((resolve) => {
      if (sessionStorage.getItem("needsUserRefresh") === "true") {
        sessionStorage.removeItem("needsUserRefresh");

        // Choose the appropriate API call based on showAllPlans
        const profileCall = showAllPlans
          ? get_profile_all_plans()
          : get_profile();

        profileCall
          .then((result) => {
            let newUserProfile = result.data;
            newUserProfile = { ...newUserProfile, ...extra_data };
            // Use the callback to wait for state update to complete
            setUserSafe(newUserProfile, (updatedUser) => {
              resolve(updatedUser);
            });
          })
          .catch((error) => {
            console.error("Error refreshing profile:", error);
            resolve(null);
          });
      } else {
        resolve(user); // Return current user if no refresh needed
      }
    });
  };

  const toggleShowAllPlans = async (value) => {
    const newValue = typeof value === "boolean" ? value : !showAllPlans;
    setShowAllPlans(newValue);

    // Trigger a profile refresh with the new setting
    sessionStorage.setItem("needsUserRefresh", "true");

    // Choose the appropriate API call based on the new value
    const profileCall = newValue ? get_profile_all_plans() : get_profile();

    try {
      const result = await profileCall;
      let newUserProfile = result.data;
      setUserSafe(newUserProfile);
      return newUserProfile;
    } catch (error) {
      console.error("Error refreshing profile:", error);
      return null;
    }
  };

  const submitEmployerPlan = async (values, planId = null, classId = null) => {
    const employer = user?.employerAdminEmployer;
    const employerId = employer?.id;
    let employerPlan = null;
    let planType = values?.plan?.planType;
    let name = "";
    if (planId) {
      employerPlan = user.employerAdminEmployer?.employerPlans.find(
        (plan) => plan.id === planId,
      );
      name = employerPlan?.name || planType || "";
    } else {
      name = planType;
    }
    if (!employerPlan) {
      name = planType;
    } else {
      planType = employerPlan?.type;
    }
    let movedPlan = employerPlan?.movedPlan;
    let midYearPlan = employerPlan?.midYearPlan;
    if (values?.movedPlan === "Yes") {
      movedPlan = true;
    } else if (values?.movedPlan === "No") {
      movedPlan = false;
    }
    if (values?.midYearPlan === "1") {
      midYearPlan = true;
    } else if (values?.midYearPlan === "0") {
      midYearPlan = false;
    }
    const { isComplete } = employerPlan || {};
    const { allowTextSubmission, textSubmissionDays } = values;
    const {
      reimbursementMethod,
      reimbursementTiming,
      reimbursementDay,
      startDate,
      endDate,
      industryEligibilityConfirmed,
    } = values;
    let eligibleIndustryCertificationTs = null;
    if (industryEligibilityConfirmed) {
      eligibleIndustryCertificationTs = new Date().getTime();
    }
    let _startDate = startDate?.dateString;
    let _endDate = endDate?.dateString;
    if (!_startDate || _startDate?.length === 0) {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const formattedMonth = String(month).padStart(2, "0");
      _startDate = `${year}-${formattedMonth}-01`;
      if (planType === "ICHRA") {
        _endDate = `${year}-12-31`;
      }
    }
    const payload = {
      isComplete: Boolean(isComplete),
      name: name,
      movedPlan: movedPlan,
      midYearPlan: midYearPlan,
      startDate: _startDate,
      endDate: _endDate,
      allowTextSubmission:
        allowTextSubmission === "Yes"
          ? true
          : allowTextSubmission === "No"
            ? false
            : null,
      textSubmissionDays: textSubmissionDays?.value,
      reimbursementDistributionMethod: reimbursementMethod?.value,
      reimbursementDistributionTiming: reimbursementTiming?.value,
      reimbursementDistributionDayOfMonth: Number(reimbursementDay)
        ? Number(reimbursementDay)
        : null,
      manageEnrollments: true,
      autoReimbursePremium: false,
      minimumEligibleHours: 30,
      gracePeriodDays: 0,
      yearEndRunoutDays: 75,
      terminationRunoutDays: 0,
      eligibleIndustryCertificationTs: eligibleIndustryCertificationTs,
    };

    let plan_ = null;
    if (planId) {
      payload.id = planId;
      plan_ = await updatePlan(payload, employerId, planId, planType);
    } else {
      plan_ = await createPlan(payload, employerId, planType);
    }
    if (employerPlan?.type === "QSEHRA" || classId) {
      let permissibleClass = getPermissibleClass(classId);
      let monthlySelfAmount = values?.monthlySelfAmount
        ? Math.round((Number(values?.monthlySelfAmount) || 0) * 100)
        : null;
      let monthlyFirstDependentAmount = values?.monthlyFirstDependentAmount
        ? Math.round((Number(values?.monthlyFirstDependentAmount) || 0) * 100)
        : null;
      let monthlyAdditionalDependentAmount =
        values?.monthlyAdditionalDependentAmount
          ? Math.round(
              (Number(values?.monthlyAdditionalDependentAmount) || 0) * 100,
            )
          : null;

      const maxBenefit = formatMaxBenefit(values);
      let predefinedClasses = [];
      if (employerPlan?.type === "QSEHRA") {
        predefinedClasses = formatQSEHRAPredefinedClasses(values);
      } else {
        predefinedClasses = formatPredefinedClasses(values);
      }
      const qualifiedBenefits = formatQualifiedBenefits(values);
      let allowanceMethod = null;
      if (values?.allowanceMethod) {
        allowanceMethod = Object.keys(values?.allowanceMethod)?.[0];
      }
      let v2 = true;
      if (permissibleClass) {
        v2 = permissibleClass?.v2;
      }
      // Radio object → boolean. { true: true } = omit the ACA minimum ("No" on screen),
      // { false: true } = apply it ("Yes"). Nothing selected is treated as apply.
      const metalOmitAcaAffordabilitySelection = Object.keys(
        values?.metalOmitAcaAffordability || {},
      ).find((key) => values.metalOmitAcaAffordability[key]);
      const metalOmitAcaAffordability =
        metalOmitAcaAffordabilitySelection === "true";
      const metalPercentDependent =
        values?.metalPercentDependent === null ||
        values?.metalPercentDependent === undefined ||
        values?.metalPercentDependent === ""
          ? null
          : Number(values.metalPercentDependent);
      const metalV2 = permissibleClass
        ? permissibleClass.metalV2 === true
        : true;
      const payload = {
        employerId,
        employerPlanId: employerPlan?.id,
        planType: planType,
        predefinedClasses: predefinedClasses,
        qualifiedBenefits: qualifiedBenefits,
        maxBenefit: maxBenefit,
        payers: [{ type: "HRA_MAX", rank: 1 }],
        v2: v2,
        monthlySelfAmount: monthlySelfAmount,
        monthlyFirstDependentAmount: monthlyFirstDependentAmount,
        monthlyAdditionalDependentAmount: monthlyAdditionalDependentAmount,
        useAcaSalary: values.useAcaSalary,
        acaSalary: Number(values?.acaSalary),
        acaMultipleSelf: Number(values?.acaMultipleSelf),
        acaMultipleSpouse: Number(values?.acaMultipleSpouse),
        acaMultipleChildren: Number(values?.acaMultipleChildren),
        acaMultipleSpouseChildren: Number(values?.acaMultipleSpouseChildren),
        acaAdjustmentType: values?.acaAdjustmentType,
        eligibleIndividuals:
          Object.keys(values?.eligibleIndividuals || {}).find(
            (key) => values.eligibleIndividuals[key],
          ) || "",
        allowanceMethod: allowanceMethod,
        metalOmitAcaAffordability: metalOmitAcaAffordability,
        metalLevel: values?.metalLevel || "",
        metalPercent: Number(values?.metalPercent) || 0,
        metalV2: metalV2,
        metalPercentDependent: metalPercentDependent,
      };
      payload.id = permissibleClass?.id;
      payload.permissibleClassId = permissibleClass?.id;
      if (permissibleClass?.id) {
        payload.id = permissibleClass.id;
        const url = `employer/${employerId}/plan/${employerPlan?.id}/class/${permissibleClass.id}`;
        await api.patch(url, payload);
      } else {
        const url = `employer/${employerId}/plan/${employerPlan?.id}/class`;
        await api.post(url, payload);
      }
    }
    setRefreshUserProfile();
    await refreshUserProfile();
    return plan_;
  };
  const createPermissibleClass = async (parentEmployerPlan) => {
    const permissibleClassPayload = {
      employerPlanId: parentEmployerPlan?.id,
      planType: parentEmployerPlan?.type,
      predefinedClasses: [],
      qualifiedBenefits: [{ type: "NONE" }],
      maxBenefit: formatMaxBenefit(null),
      payers: [{ type: "HRA_MAX", rank: 1 }],
      metalV2: true,
      v2: true,
    };
    const employerId = user?.employerAdminEmployer?.id;
    const url = `employer/${employerId}/plan/${parentEmployerPlan?.id}/class`;
    const response = await api.post(url, permissibleClassPayload);
    setRefreshUserProfile();
    await refreshUserProfile();
    return response?.data;
  };

  function formatPredefinedClasses(values) {
    const { predefinedClasses, ratingAreas, otherRatingArea } = values;
    if (!predefinedClasses) {
      return [{ type: "ALL_ELIGIBLE_EMPLOYEES", isExcluded: false }];
    }

    return Object.entries(predefinedClasses)?.reduce(
      (predefinedClasses, [predefinedClass, isIncluded]) => {
        let value = null;
        if (typeof isIncluded === "boolean" || predefinedClass === "RATING") {
          value = { type: predefinedClass, isExcluded: !isIncluded };
        }
        if (predefinedClass === "RATING") {
          if (!ratingAreas || !Object.entries(ratingAreas).length) {
            value.states = [];
          } else {
            value.states = Object.entries(ratingAreas)?.reduce(
              (ratingAreas, [ratingArea, selected]) => {
                if (selected) ratingAreas.push(ratingArea);
                return ratingAreas;
              },
              [],
            );
          }

          if (value.states?.includes("other")) {
            value.other = otherRatingArea;
          }
        }
        predefinedClasses.push(value);
        return predefinedClasses;
      },
      [],
    );
  }

  function formatQSEHRAPredefinedClasses(values) {
    const { excludedClasses } = values;
    if (!excludedClasses) {
      return [{ type: "NO_EXCLUDED_EMPLOYEES", isExcluded: true }];
    }

    return Object.entries(excludedClasses)?.reduce(
      (excludedClasses, [excludedClass, isExcluded]) => {
        let value = null;
        value = { type: excludedClass, isExcluded: isExcluded };
        excludedClasses.push(value);
        return excludedClasses;
      },
      [],
    );
  }

  function formatQualifiedBenefits(values) {
    const { qualifiedBenefits, otherBenefit } = values;

    if (!Object.entries(qualifiedBenefits || {}).length) {
      return [{ type: "NONE" }];
    }

    return Object.entries(qualifiedBenefits)?.reduce(
      (qualifiedBenefits, [benefit, selected]) => {
        if (selected) {
          if (benefit === "OTHER")
            qualifiedBenefits.push({
              type: benefit,
              description: otherBenefit,
            });
          else qualifiedBenefits.push({ type: benefit });
        }

        return qualifiedBenefits;
      },
      [],
    );
  }

  function formatMaxBenefit(values) {
    const benefits = [
      { type: "SELF", formKey: "selfAmount" },
      { type: "SPOUSE", formKey: "spouseAmount" },
      { type: "CHILDREN", formKey: "childAmount" },
      { type: "SPOUSE_CHILDREN", formKey: "allAmount" },
    ];

    return {
      type: Object.keys(values?.maxBenefit || {})?.[0] || "SELF",

      ...benefits.reduce((benefits, benefit) => {
        let { type, formKey } = benefit;

        const amount = Math.round((Number(values?.[formKey]) || 0) * 1200);
        benefits[type] = { amount };

        for (
          let age = MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
          age <= MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE;
          age++
        ) {
          const inputKey = `${formKey}${age}`;
          const inputValue = values?.[inputKey] || 0;
          const amount = Math.round(Number(inputValue) || 0);
          const key = `amount${age}`;
          benefits[type][key] = amount;
        }
        return benefits;
      }, {}),
    };
  }
  async function createPlan(payload, employerId, planType) {
    const url = `employer/${employerId}/plan/v2/${planType}`;
    const request = await api.post(url, payload);
    return request.data;
  }

  async function updatePlan(payload, employerId, planId, planType) {
    const url = `employer/${employerId}/plan/v2/${planType}/${planId}`;
    const request = await api.patch(url, payload);
    return request.data;
  }
  const ensureAdminSnapshot = (snapshot) => {
    if (!snapshot) return;
    const current = storage.readJSON(ADMIN_SNAPSHOT_KEY);
    if (current) return;
    const token = snapshot?.token || storage.read("token");
    storage.writeJSON(ADMIN_SNAPSHOT_KEY, { ...snapshot, token });
  };

  const clearEmployeeScopedStorage = () => {
    storage.clearBySuffix("employee");
  };
  const getUserPlan = (userPlanId) => {
    if (!user || !Array.isArray(user.userPlans)) return null;
    const userPlan = user.userPlans.find((plan) => plan.id === userPlanId);
    return userPlan || null;
  };
  const getHealthInsurance = (planId, healthInsuranceId) => {
    const userPlan = getUserPlan(planId);
    if (!userPlan || !Array.isArray(userPlan.healthInsurance)) return null;
    const healthInsurance = userPlan.healthInsurance.find(
      (insurance) => insurance.id === healthInsuranceId,
    );
    return healthInsurance || null;
  };
  /** One of the user's insurances (health insurance v2). Insurance belongs to the user, not a plan. */
  const getUserHealthInsurance = (userHealthInsuranceId) => {
    if (!user || !Array.isArray(user.userHealthInsurances)) return null;
    const userHealthInsurance = user.userHealthInsurances.find(
      (insurance) => insurance.id === userHealthInsuranceId,
    );
    return userHealthInsurance || null;
  };
  const getPartner = () => {
    if (!user) return null;
    return user.partner || null;
  };
  const getEmployerPlans = () => {
    if (!user || !Array.isArray(user.userPlans)) return [];
    return user.employerAdminEmployer?.employerPlans;
  };
  const getEmployer = () => {
    if (!user) return null;
    return user.employerAdminEmployer || null;
  };
  const getEmployerPlan = (planId) => {
    if (!user || !planId || !user.employerAdminEmployer?.employerPlans)
      return null;
    const employerPlan = user.employerAdminEmployer?.employerPlans.find(
      (plan) => plan.id === planId,
    );
    return employerPlan || null;
  };
  const getEmployerPlanId = () => {
    return employerPlanId;
  };
  const getPermissibleClassId = () => {
    return permissibleClassId;
  };
  const getPermissibleClass = (permissibleClassId) => {
    if (
      !user ||
      !permissibleClassId ||
      !user.employerAdminEmployer?.employerPlans
    )
      return null;
    let permissibleClass = null;
    if (user?.employerAdminEmployer?.employerPlans?.length > 0) {
      for (const plan of user.employerAdminEmployer?.employerPlans) {
        if (plan.permissibleClasses?.length > 0) {
          const checkPermissibleClass = plan.permissibleClasses.find(
            (pc) => pc.id === permissibleClassId,
          );
          if (checkPermissibleClass) {
            permissibleClass = checkPermissibleClass;
            break;
          }
        }
      }
    }
    return permissibleClass || null;
  };
  const getUserEmployer = () => {
    if (!user) return null;
    return user.employer || null;
  };
  const resolvePostLoginRoute = async (profile) => {
    const candidate = profile ?? user;
    if (!candidate) return "/user";

    const source =
      candidate instanceof User ? candidate : User.fromApi(candidate);
    let url = "/user";
    if (source.showUser()) url = "/user";
    else if (source.showEmployers()) url = "/employers-dashboard";
    else if (source.showPartner()) url = "/partner-dashboard";
    else if (source.showReferrals()) url = "/referrals-dashboard";
    else if (source.showEmployer()) {
      setRefreshUserProfile();
      const updatedUser = await refreshUserProfile();
      const employer = updatedUser?.employerAdminEmployer || null;
      const signupStatus = employer?.signupStatus;
      let nextSection = "";
      if (
        signupStatus?.companyInfo?.required &&
        !signupStatus?.companyInfo?.complete
      ) {
        nextSection = "company_details";
      } else if (
        signupStatus?.plans?.required &&
        !signupStatus?.plans?.complete
      ) {
        nextSection = "plans";
      } else if (
        signupStatus?.reimbursements?.required &&
        !signupStatus?.reimbursements?.complete
      ) {
        nextSection = "reimbursements";
      } else if (
        signupStatus?.billing?.required &&
        !signupStatus?.billing?.complete
      ) {
        nextSection = "billing";
      } else if (
        signupStatus?.documents?.required &&
        !signupStatus?.documents?.complete
      ) {
        nextSection = "documents";
      }
      if (nextSection?.length > 0) {
        url = getUrl(employer, null, null, nextSection, 0, true);
      } else {
        url = `/employer/${employer?.id}`;
      }
    } else if (source.showUsers()) url = "/users-dashboard";
    else if (source.showOps()) url = "/ops-dashboard";
    else if (source.showUsage()) url = "/usage-dashboard";
    else if (source.showData()) url = "/search-dashboard";
    else if (source.showSystem()) url = "/system-dashboard";
    console.log("postLogin route url", url);
    return url;
  };

  // Load profile if token exists
  const ranOnce = useRef(false);
  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;
    const token = storage.read("token");
    if (!token) return;
    get_profile()
      .then((res) => setUserSafe(res.data))
      .catch(() => setUser(null));
  }, [setUserSafe]);

  const becomeEmployer = async (employerId) => {
    setImpersonating(true);
    try {
      if (!user?.id) {
        throw new Error("No active user session found.");
      }

      await attach_employer(employerId, { userId: user.id });
      // Refetch profile and set user
      setRefreshUserProfile();
      const updatedUser = await refreshUserProfile();
      let url = null;
      const employer = updatedUser?.employerAdminEmployer || null;
      const employerSignupStatus = employer?.signupStatus;
      console.log(
        "becomeEmployer: Updated user after attaching employer:",
        updatedUser,
      );
      console.log("becomeEmployer: Employer = ", employer);
      if (
        updatedUser?.partnerId &&
        employer?.partnerId === updatedUser?.partnerId &&
        employerSignupStatus?.plans?.complete
      ) {
        url = `/employer/${employer?.id}`;
      } else if (
        employerSignupStatus?.isSetupComplete?.required &&
        !employerSignupStatus?.isSetupComplete?.complete
      ) {
        url = getUrl(employer, null, null, "company_details", 0, true);
      } else {
        url = `/employer/${employer?.id}`;
      }
      console.log("becomeEmployer: Resolved post-impersonation route:", url);
      return { success: true, route: url };
    } catch (error) {
      toast.error(error?.message || "Failed to switch employer.");
      return { success: false, error: error.message };
    } finally {
      setImpersonating(false);
    }
  };

  const becomeUser = async (targetUser) => {
    setImpersonating(true);
    try {
      const adminSnapshot = user ?? null;
      if (adminSnapshot) {
        ensureAdminSnapshot(adminSnapshot);
      }

      // Where impersonation began in history. Only the first becomeUser records it,
      // the same way ensureAdminSnapshot keeps the first snapshot.
      if (storage.read(IMPERSONATION_START_IDX_KEY) == null) {
        const idx = window.history.state?.idx;
        if (typeof idx === "number") {
          storage.write(IMPERSONATION_START_IDX_KEY, String(idx));
        }
      }

      clearEmployeeScopedStorage();

      const targetId =
        typeof targetUser === "object"
          ? (targetUser?.user?.id ?? targetUser?.id)
          : targetUser;

      if (!targetId) {
        throw new Error("Unable to determine selected user.");
      }

      const response = await loginById(targetId);
      const impersonated = response?.data;
      if (impersonated?.token) {
        storage.write("token", impersonated.token);
      }
      if (impersonated) {
        // Fetch fresh profile for impersonated user and set as current user
        const res = await get_profile();
        let impersonatedProfile = res?.data ?? null;
        impersonatedProfile = {
          ...impersonatedProfile,
          patientId: impersonated.patientId,
        };
        if (impersonatedProfile) {
          setUserSafe(impersonatedProfile);
        }
        const destination = await resolvePostLoginRoute(impersonatedProfile);
        const name =
          `${impersonatedProfile?.firstName ?? ""} ${impersonatedProfile?.lastName ?? ""}`.trim();
        toast.info(`Now logged in as ${name || "selected user"}.`);
        return {
          success: true,
          user: impersonatedProfile ?? impersonated,
          route: destination,
        };
      }

      return {
        success: true,
        user: impersonated,
        route: await resolvePostLoginRoute(),
      };
    } catch (error) {
      toast.error(error?.message || "Failed to switch user.");
      return { success: false, error: error.message };
    } finally {
      setImpersonating(false);
    }
  };
  const getEmailDomain = ({ email }) => {
    const emailParts = email.toLowerCase().split("@");
    if (emailParts.length === 2) return emailParts[1].split(".")[0];
  };

  const exitUser = async () => {
    try {
      const adminData = storage.readJSON(ADMIN_SNAPSHOT_KEY);
      if (!adminData) {
        throw new Error("No admin session found. Please log back in.");
      }

      storage.remove(ADMIN_SNAPSHOT_KEY);
      storage.remove(IMPERSONATION_START_IDX_KEY);

      if (adminData?.token) {
        storage.write("token", adminData.token);
      }

      setUserSafe(adminData);
      clearEmployeeScopedStorage();
      const destination = await resolvePostLoginRoute(adminData);
      toast.info(
        `Returned to admin account${adminData?.firstName ? ` ${adminData.firstName}` : ""}.`,
      );
      return { success: true, user: adminData, route: destination };
    } catch (error) {
      toast.error(error?.message || "Failed to exit impersonation.");
      return { success: false, error: error.message };
    }
  };

  const sendMessage = async ({ payload, disableNotification }) => {
    const emailDomain = getEmailDomain({ email: payload.email });
    if (EMAIL_DOMAIN_BLACKLIST.includes(emailDomain)) return;

    if (user?.id) {
      payload.userId = user?.id;
      payload.email = user?.email;
    }

    await send_message(payload);

    if (!disableNotification) {
      toast.info("Message sent successfully. We will respond to you shortly.");
    }

    return true;
  };
  const logout = async () => {
    await signOut();
    localStorage.clear();
    setUser(null);
    setShowAllPlans(false);
  };
  const isSysAdmin = () =>
    user instanceof User
      ? user.isSysAdmin
      : Boolean(user?.roles?.includes("SYSADMIN"));
  const isDeveloper = () =>
    user instanceof User
      ? user.isDeveloper
      : Boolean(user?.roles?.includes("DEVELOPER"));
  const isUser = () =>
    user instanceof User
      ? user.showUser()
      : Array.isArray(user?.userPlans) && user.userPlans.length > 0;
  const isEmployerAdmin = () =>
    user instanceof User
      ? user.showEmployer()
      : Boolean(get(user, "employerAdminEmployer.id"));
  const isPartner = () =>
    user instanceof User ? user.showPartner() : Boolean(user?.partnerId);
  const isReferralAccount = () =>
    user instanceof User
      ? user.showReferrals()
      : Boolean(user?.isReferralAccount);
  const isSeanOrCustomer = () => {
    let isSean = false;
    let isCustomer = false;
    let isYvonne = false;
    if (user?.email === "sean@salusion.com") {
      isSean = true;
    }
    if (user?.email === "yvonne@salusion.com") {
      isYvonne = true;
    }
    if (!isSysAdmin()) {
      isCustomer = true;
    }
    return isSean || isYvonne || isCustomer;
  };
  const isCustomer = () => {
    let isCustomer = true;
    if (isSysAdmin()) {
      isCustomer = false;
    }
    return isCustomer;
  };
  const getMasterAdmin = () => {
    const adminSnapshot = storage.readJSON(ADMIN_SNAPSHOT_KEY);
    if (adminSnapshot) {
      // Impersonating — check the original admin
      const isAdmin = adminSnapshot.roles?.includes("SYSADMIN");
      const isSean =
        adminSnapshot.email === "sean@salusion.com" ||
        adminSnapshot.email === "yvonee@salusion.com";
      return isAdmin && !isSean;
    }
    // Not impersonating — check current user
    if (user) {
      return isSysAdmin() && user.email !== "sean@salusion.com";
    }
    return false;
  };
  const showUser = () => (user instanceof User ? user.showUser() : isUser());
  const showEmployer = () =>
    user instanceof User ? user.showEmployer() : isEmployerAdmin();
  const showReferrals = () =>
    user instanceof User ? user.showReferrals() : isReferralAccount();
  const showPartner = () =>
    user instanceof User ? user.showPartner() : isPartner();
  const showUsers = () =>
    user instanceof User ? user.showUsers() : isSysAdmin();
  const showEmployers = () =>
    user instanceof User ? user.showEmployers() : isSysAdmin();
  const showOps = () => (user instanceof User ? user.showOps() : isSysAdmin());
  const showUsage = () =>
    user instanceof User ? user.showUsage() : isSysAdmin();
  const showData = () =>
    user instanceof User ? user.showData() : isDeveloper();
  const showSystem = () =>
    user instanceof User ? user.showSystem() : isDeveloper();
  const activeRole = () => {
    return storage.read("activeRole");
  };
  return (
    <UserContext.Provider
      value={{
        user,
        getUserPlan,
        getHealthInsurance,
        getUserHealthInsurance,
        getPartner,
        getEmployer,
        getEmployerPlans,
        getEmployerPlan,
        setEmployerPlanId,
        getEmployerPlanId,
        submitEmployerPlan,
        createPermissibleClass,
        setPermissibleClassId,
        getPermissibleClassId,
        getPermissibleClass,
        setUser: setUserSafe,
        setRefreshUserProfile,
        refreshUserProfile,
        getUserEmployer,
        logout,
        becomeEmployer,
        becomeUser,
        activeRole: activeRole(),
        isSeanOrCustomer: isSeanOrCustomer(),
        isCustomer: isCustomer(),
        isSysAdmin: isSysAdmin(),
        isPartner: isPartner(),
        isImpersonating,
        isMasterAdmin: getMasterAdmin(),
        showUser: showUser(),
        showEmployer: showEmployer(),
        showPartner: showPartner(),
        showReferrals: showReferrals(),
        showUsers: showUsers(),
        showEmployers: showEmployers(),
        showOps: showOps(),
        showUsage: showUsage(),
        showData: showData(),
        showSystem: showSystem(),
        isDeveloper: isDeveloper(),
        exitUser,
        resolvePostLoginRoute,
        sendMessage,
        impersonating,
        showAllPlans,
        toggleShowAllPlans,
        stoppedTtcSourceIds,
        markTtcSourceStopped,
        markTtcSourceActive,
      }}
    >
      {children}
      {impersonating && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            zIndex: 2000,
            backdropFilter: "blur(2px)",
            pointerEvents: "all",
          }}
        >
          <CircularProgress sx={{ color: "#6C63FF" }} />
        </Box>
      )}
    </UserContext.Provider>
  );
};
